import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import scrollIntoView from "scroll-into-view-if-needed";
import { useBetween } from "use-between";

import { Button } from "@/components/button";
import { categoryProps } from "@/components/editor/block-categories-panel/categories";
import cursorImage from "@/components/editor/preference-panel/layout/cursor.png";
import layoutDefaultPlaceholder from "@/components/editor/preference-panel/layout/layout-default-placeholder.jpg";
import { Edit } from "@/components/icons/edit";
import { Trash } from "@/components/icons/trash";
import { Card } from "@/components/shared/card";
import useAnalytics from "@/hooks/use-analytics";
import useSelectionMenu from "@/hooks/use-selection-menu";

import Layers from "../../layers/layers-component";
import {
  Container,
  CursorImage,
  Image,
  Outline,
  Sticky,
} from "./layout-default-card-component-styles";

const LayoutDefaultCard = () => {
  const { logAnalytics } = useAnalytics();
  const { openSelectionMenu } = useBetween(useSelectionMenu);
  const { t } = useTranslation();

  const handleAddBlock = React.useCallback(() => {
    const category = categoryProps[0].type;

    openSelectionMenu(category, true);
    logAnalytics("CANVAS_EDITOR_MENU_ITEM_VIEWED", {
      menu_item: category,
    });
  }, [logAnalytics, openSelectionMenu]);

  const { query, actions } = useEditor();
  const { nodeId, childNodes } = useNode((node) => {
    return {
      nodeId: node.id,
      childNodes: node.getChildNodes().length,
    };
  });

  return (
    <>
      {childNodes === 0 ? (
        <Card
          className="relative mt-1 flex w-full flex-col items-center"
          data-testid="layout-default-card"
        >
          <Container>
            <Outline />
            <Image
              src={layoutDefaultPlaceholder.src}
              alt="Default image for layout panel"
              data-testid="layout-default-card-image"
              draggable="false"
            />
            <CursorImage
              src={cursorImage.src}
              alt="cursor icon"
              draggable="false"
            />
          </Container>
          <p
            className="my-4 text-center text-sm text-gray-800"
            data-testid="layout-default-card-text"
          >
            {t("preference-panel.drag-drop-blocks")}
          </p>
        </Card>
      ) : (
        <>
          <div className="mb-2 flex">
            <p className="text-sm leading-6">
              {t("preference-panel.page-blocks")}
            </p>
          </div>
          <Layers
            query={query}
            actions={actions}
            parentId={nodeId}
            onItemRender={(node) => (
              <div className="ml-2 flex grow items-center justify-between p-2">
                <p className="text-sm">{node.custom.displayName}</p>

                <div className="flex">
                  {childNodes > 1 && (
                    <button
                      className="flex cursor-pointer items-center justify-center p-1 text-gray-650 hover:rounded hover:bg-gray-200 hover:text-gray-800"
                      onClick={() => {
                        actions.delete(node.id);
                      }}
                      title={t("common.delete")}
                    >
                      <Trash className="size-4" />
                    </button>
                  )}

                  <button
                    className="flex cursor-pointer items-center justify-center p-1 text-gray-650 hover:rounded hover:bg-gray-200 hover:text-gray-800"
                    onClick={(event) => {
                      actions.selectNode(node.id);

                      const nodeElement = query.node(node.id).getDOM();
                      if (nodeElement) {
                        scrollIntoView(nodeElement, { behavior: "smooth" });
                      }

                      if (event.detail === 0) {
                        window.requestAnimationFrame(() => {
                          document
                            .querySelector<HTMLButtonElement>(
                              '[data-testid="block-outline-copy"]'
                            )
                            ?.focus();
                        });
                      }
                    }}
                    title={t("common.edit")}
                  >
                    <Edit className="size-4" />
                  </button>
                </div>
              </div>
            )}
          />
        </>
      )}

      <Sticky>
        <Button
          size="small"
          onClick={handleAddBlock}
          data-testid="selection-menu-button"
          isInline
        >
          {t("preference-panel.add-blocks")}
        </Button>
      </Sticky>
    </>
  );
};

export default LayoutDefaultCard;
